import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Group,
  Image,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { motion } from 'motion/react';
import { ApiError } from '../api/client.js';
import { authApi } from '../api/endpoints.js';
import { brand } from '../theme/index.js';
import classes from './login.module.css';

const SearchSchema = z.object({
  /** Where to return to after signing in; set by the server's 302. */
  next: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: SearchSchema,
  component: LoginPage,
});

/** Only same-origin paths — never bounce a user to an attacker's URL. */
function safeNext(next: string | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//')) return '/';
  return next;
}

function LoginPage() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value) ? null : 'Enter a valid email address'),
      password: (value) => (value.length > 0 ? null : 'Enter your password'),
    },
  });

  const submit = form.onSubmit(async (values) => {
    setBusy(true);
    setError(null);
    try {
      await authApi.login(values.email, values.password);
      // Full navigation so the app boots with the new session and the rotated
      // CSRF cookie, rather than re-using the signed-out router context.
      window.location.replace(safeNext(next));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not reach the server. Check your connection and try again.',
      );
      form.setFieldValue('password', '');
      setBusy(false);
    }
  });

  return (
    <Box className={classes.page}>
      <Center h="100%" p="md">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '100%', maxWidth: 400 }}
        >
          <Stack gap="lg">
            <Group justify="center">
              <Image src={brand.logo.mark} alt="" w={52} h={52} />
            </Group>

            <Stack gap={4} align="center">
              <Title order={2} c="var(--rm-text-primary)">
                {brand.productName}
              </Title>
              <Text size="sm" c="var(--rm-text-muted)">
                Sign in to see what the team is working on.
              </Text>
            </Stack>

            <Card withBorder radius="lg" p="lg" className={classes.card}>
              <form onSubmit={submit} noValidate>
                <Stack gap="md">
                  {error && (
                    <Alert color="red" variant="light" radius="md" role="alert">
                      {error}
                    </Alert>
                  )}

                  <TextInput
                    label="Email"
                    placeholder="you@rainmakermarketing.co.za"
                    type="email"
                    autoComplete="username"
                    autoFocus
                    required
                    {...form.getInputProps('email')}
                  />

                  <PasswordInput
                    label="Password"
                    autoComplete="current-password"
                    required
                    {...form.getInputProps('password')}
                  />

                  <Button type="submit" fullWidth loading={busy} mt="xs">
                    Sign in
                  </Button>
                </Stack>
              </form>
            </Card>

            <Text size="xs" c="var(--rm-text-muted)" ta="center">
              Need an account?{' '}
              <Anchor size="xs" href="mailto:renier@rainmakermarketing.co.za">
                Ask an admin for an invite
              </Anchor>
              .
            </Text>
          </Stack>
        </motion.div>
      </Center>
    </Box>
  );
}
